import { type ColumnDef } from '@tanstack/react-table';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { formatTypeLabel } from '@/hooks/data/inspector/useUploadStore';
import { type GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface FormatColumnOptions<T> {
  id: string;
  accessorFn: (row: T) => string;
  accessorKey?: keyof T;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  label?: string;
  galleryView: GalleryView;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  dataCy?: string;
  /** Rows whose value is a mimetype should supply this, since some mimetypes cover several formats. */
  fileNameFn?: (row: T) => string | undefined | null;
}

export const FormatColumn = <T extends Record<string, any>>(opts: FormatColumnOptions<T>): ColumnDef<T> => {
  const {
    id,
    accessorFn,
    accessorKey,
    label,
    galleryView,
    size = 90,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 30,
    maxSize = 120,
    dataCy,
    fileNameFn,
  } = opts;

  return {
    id,
    enableSorting,
    enableHiding,
    minSize,
    maxSize,
    accessorFn,
    accessorKey,
    header: () => label,
    meta: {
      cellClassName: cn('capitalize', cellClassName),
      ...(headerClassName && { headerClassName: headerClassName }),
    },
    size,
    cell: ({ getValue, row }) => {
      const raw = getValue();
      // e.g., 'JPEG', 'PNG', etc.

      if (raw === null || raw === undefined || raw === '') {
        return '';
      }

      const rawType = String(raw);
      const type = formatTypeLabel(rawType, fileNameFn?.(row.original));

      const content = (
        <div
          className={cn('uppercase text-text-gray', {
            'line-clamp-1! wrap-anywhere': type && type?.length > 10,
          })}
          data-cy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${id}`}
        >
          {type}
        </div>
      );

      return rawType.length > 10 ? (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipPortal>
            <TooltipContent className="max-w-44 break-all">{rawType}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ) : (
        content
      );
    },
  };
};
